import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  Pencil,
  Search,
  Loader2,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { analyzeCF } from "@/utils/codiceFiscale";
import api from "@/api";

/**
 * AnagraficaConsistencyDialog — v1.4.1 · 17/07/2026 sera.
 *
 * Report di qualità dati sull'anagrafica clienti. Per ogni cliente
 * PRIVATO/DITTA con CF valido, controlla la coerenza fra il codice
 * catastale (posizioni 11-14 del CF) e il "Comune di Nascita" salvato.
 *
 * Colonne del report:
 *   - Cliente, CF, Comune inserito, Comune atteso (da CF), Azione.
 *
 * Il pulsante "Correggi automaticamente" sovrascrive `birthPlace` con
 * il comune corretto ricavato dal CF (PUT /api/clients/{id}).
 *
 * Il pulsante "Modifica" delega al parent l'apertura del ClientDialog
 * classico per correzioni manuali.
 */
export default function AnagraficaConsistencyDialog({
  isOpen,
  onOpenChange,
  clients,
  onEditClient,
  onReloadClients,
}) {
  const [scanning, setScanning] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setScanning(true);
    // Micro-yield per non bloccare la UI su liste lunghe (127+ clienti).
    const t = setTimeout(() => {
      const rows = [];
      for (const c of clients || []) {
        const cf = (c.taxCode || "").trim();
        if (!cf || cf.length < 16) continue;
        // Skip aziende (CF numerico != validazione persona)
        if (c.clientType === "Azienda") continue;
        const analysis = analyzeCF(
          cf,
          c.fullName,
          c.lastName,
          c.firstName,
          c.birthPlace,
        );
        if (!analysis?.valid) continue;
        const check = analysis.birthPlaceCheck || {};
        if (check.status === "mismatch" || check.status === "unknown") {
          rows.push({
            id: c.id,
            fullName: c.fullName || `${c.lastName || ""} ${c.firstName || ""}`.trim(),
            taxCode: cf,
            currentBirthPlace: c.birthPlace || "",
            expectedName: check.expectedName || "",
            expectedFull: analysis.birthPlace || "",
            status: check.status,
            clientType: c.clientType || "",
          });
        }
      }
      // Ordina: prima "mismatch" (fixabili automaticamente), poi "unknown".
      rows.sort((a, b) => {
        if (a.status !== b.status) return a.status === "mismatch" ? -1 : 1;
        return a.fullName.localeCompare(b.fullName, "it");
      });
      setReportRows(rows);
      setScanning(false);
    }, 30);
    return () => clearTimeout(t);
  }, [isOpen, clients]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reportRows;
    return reportRows.filter(
      (r) =>
        r.fullName.toLowerCase().includes(q) ||
        r.taxCode.toLowerCase().includes(q) ||
        (r.currentBirthPlace || "").toLowerCase().includes(q) ||
        (r.expectedName || "").toLowerCase().includes(q),
    );
  }, [reportRows, query]);

  const stats = useMemo(
    () => ({
      total: reportRows.length,
      mismatch: reportRows.filter((r) => r.status === "mismatch").length,
      unknown: reportRows.filter((r) => r.status === "unknown").length,
    }),
    [reportRows],
  );

  const handleAutofix = async (row) => {
    if (!row.expectedFull) {
      toast.error("Impossibile correggere: comune atteso mancante");
      return;
    }
    if (
      !window.confirm(
        `Sostituire il Comune di Nascita di ${row.fullName}\n\nda:  ${row.currentBirthPlace || "(vuoto)"}\na:   ${row.expectedFull}\n\nProseguire?`,
      )
    ) {
      return;
    }
    try {
      setFixingId(row.id);
      await api.updateClient(row.id, { birthPlace: row.expectedFull });
      toast.success(`Comune corretto per ${row.fullName}`);
      setReportRows((prev) => prev.filter((r) => r.id !== row.id));
      if (onReloadClients) onReloadClients();
    } catch (err) {
      toast.error(err?.message || "Errore durante la correzione");
    } finally {
      setFixingId(null);
    }
  };

  const handleEdit = (row) => {
    const client = (clients || []).find((c) => c.id === row.id);
    if (!client) return;
    onOpenChange(false);
    if (onEditClient) setTimeout(() => onEditClient(client), 200);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0"
        data-testid="anagrafica-consistency-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            Qualità Anagrafica — CF ↔ Comune di Nascita
          </DialogTitle>
          <DialogDescription>
            Individua i clienti in cui il Comune di Nascita salvato non
            corrisponde al codice catastale del Codice Fiscale (probabili
            typo storici da bonificare).
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3 border-b bg-slate-50/60 flex flex-wrap items-center gap-3">
          <Badge className="bg-slate-800 text-white">Analizzati: {clients?.length || 0}</Badge>
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            Da correggere: {stats.total}
          </Badge>
          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
            Mismatch: {stats.mismatch}
          </Badge>
          <Badge className="bg-slate-100 text-slate-700 border-slate-200">
            Codice sconosciuto: {stats.unknown}
          </Badge>
          <div className="ml-auto relative w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca cliente / CF / comune…"
              className="pl-8"
              data-testid="consistency-search"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {scanning ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-8">
              <Loader2 className="h-4 w-4 animate-spin" />
              Analisi in corso su {clients?.length || 0} clienti…
            </div>
          ) : reportRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 p-12 text-slate-500">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <p className="font-semibold text-emerald-700">
                Anagrafica pulita! Nessuna incoerenza CF ↔ Comune rilevata.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-sm text-slate-500 p-8">
              Nessun risultato per «{query}».
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold text-slate-700">Cliente</th>
                  <th className="px-4 py-2 font-semibold text-slate-700">Codice Fiscale</th>
                  <th className="px-4 py-2 font-semibold text-slate-700">Comune inserito</th>
                  <th className="px-4 py-2 font-semibold text-slate-700">Comune atteso (da CF)</th>
                  <th className="px-4 py-2 font-semibold text-slate-700 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-amber-50/30"
                    data-testid={`consistency-row-${row.id}`}
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{row.fullName}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">
                        {row.clientType || "cliente"}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{row.taxCode}</td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 text-orange-700">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span className="truncate max-w-[220px]" title={row.currentBirthPlace}>
                          {row.currentBirthPlace || <em className="text-slate-400">vuoto</em>}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {row.status === "unknown" ? (
                        <span className="text-slate-500 italic">Codice catastale non riconosciuto</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span className="truncate max-w-[220px]" title={row.expectedFull}>
                            {row.expectedFull}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {row.status === "mismatch" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAutofix(row)}
                          disabled={fixingId === row.id}
                          className="mr-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          data-testid={`consistency-autofix-${row.id}`}
                        >
                          {fixingId === row.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Wand2 className="h-3.5 w-3.5 mr-1" />
                          )}
                          Correggi
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(row)}
                        data-testid={`consistency-edit-${row.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        Apri
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-slate-50/40">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="consistency-close"
          >
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
